import {
  VISIBILITY_OPERATORS, getValueType, getConditionValue, operatorNeedsValue,
} from './fieldConditions';

export const FIELD_ACTION_TYPES = ['lookup', 'set_values', 'clear_values'];
export const FIELD_ACTION_SOURCES = ['zipcode', 'zipcode_cnbv', 'catalog'];

export const getFieldActions = (field = {}) => {
  let { extra } = field;
  if (typeof extra === 'string') {
    try { extra = JSON.parse(extra); } catch (_error) { extra = {}; }
  }
  const configured = field.actions ?? extra?.actions;
  return Array.isArray(configured) ? configured : [];
};

export const getActionManagedFields = (template = []) => new Set(template.flatMap((field) => {
  const actions = getFieldActions(field);
  return actions.length ? [field.fieldname, ...actions.flatMap((action) =>
    (action.mappings || []).map((mapping) => mapping.fieldname))] : [];
}));

export const createFieldAction = () => ({
  type: 'lookup',
  enabled: true,
  trigger: {
    operator: 'has_value',
    length: 5,
  },
  source: {
    type: 'zipcode',
  },
  resetOnChange: true,
  mappings: [{
    fieldname: '',
    mode: 'options',
    valuePath: '',
    labelPath: '',
    autoSelectSingle: true,
  }],
});

export const serializeFieldActions = (actions = []) => actions.map((action) => {
  const trigger = {
    operator: action.trigger.operator,
    ...(operatorNeedsValue(action.trigger.operator) ? {
      value: getConditionValue(action.trigger),
    } : {}),
    ...(action.trigger.length !== undefined && action.trigger.length !== ''
      ? {
        length: Number(action.trigger.length),
      } : {}),
  };
  return {
    type: action.type,
    enabled: action.enabled !== false,
    trigger,
    ...(action.type === 'lookup' ? {
      source: action.source,
      resetOnChange: action.resetOnChange !== false,
    } : {}),
    mappings: action.mappings.map((mapping) => {
      const result = {
        fieldname: mapping.fieldname,
      };
      if (action.type === 'lookup') return {
        ...result,
        mode: mapping.mode,
        valuePath: mapping.valuePath.trim(),
        ...(mapping.mode === 'options' ? {
          labelPath: (mapping.labelPath || mapping.valuePath).trim(),
          autoSelectSingle: mapping.autoSelectSingle !== false,
        } : {}),
      };
      if (action.type === 'clear_values') return result;
      return {
        ...result,
        valueFrom: mapping.valueFrom || 'constant',
        ...(mapping.valueFrom === 'field' ? {
          sourceFieldname: mapping.sourceFieldname,
        } : {}),
        ...(!mapping.valueFrom || mapping.valueFrom === 'constant'
          ? {
            value: getConditionValue({
              operator: 'equals',
              value: mapping.value,
              valueType: mapping.valueType,
            }),
          } : {}),
      };
    }),
  };
});

export const validateFieldActions = (actions = [], fields = []) => {
  const errors = {};
  const names = new Set(fields.map((field) => field.fieldname));
  if (!Array.isArray(actions)) return {
    actions: 'invalid_actions',
  };
  actions.forEach((action, index) => {
    const key = `actions.${index}`;
    if (!FIELD_ACTION_TYPES.includes(action.type)) errors[`${key}.type`] = 'select_type';
    const trigger = action.trigger || {};
    if (!VISIBILITY_OPERATORS.includes(trigger.operator)) errors[`${key}.operator`] = 'select_operator';
    if (operatorNeedsValue(trigger.operator)) {
      if (trigger.value === undefined || trigger.value === null || String(trigger.value).trim() === '') errors[`${key}.value`] = 'enter_value';
      else if (getValueType(trigger) === 'number' && !Number.isFinite(Number(trigger.value))) errors[`${key}.value`] = 'invalid_number';
      else if (getValueType(trigger) === 'boolean' && ![true, false, 'true', 'false'].includes(trigger.value)) errors[`${key}.value`] = 'invalid_boolean';
    }
    if (trigger.length !== undefined && trigger.length !== '' && (!Number.isInteger(Number(trigger.length)) || Number(trigger.length) < 1)) errors[`${key}.length`] = 'invalid_length';
    if (action.type === 'lookup') {
      if (!FIELD_ACTION_SOURCES.includes(action.source?.type)) errors[`${key}.source`] = 'select_source';
      if (action.source?.type === 'catalog' && !action.source.catalog) errors[`${key}.catalog`] = 'select_catalog';
      if (action.source?.type === 'catalog' && (!/^[a-zA-Z_][\w]*$/.test(action.source.queryParam || '') || ['catalog', 'fields'].includes(action.source.queryParam))) errors[`${key}.queryParam`] = 'invalid_parameter';
    }
    if (!Array.isArray(action.mappings) || !action.mappings.length) errors[`${key}.mappings`] = 'add_mapping';
    const targets = new Set();
    (action.mappings || []).forEach((mapping, row) => {
      const rowKey = `${key}.mappings.${row}`;
      if (!names.has(mapping.fieldname) || targets.has(mapping.fieldname)) errors[`${rowKey}.fieldname`] = 'select_target';
      targets.add(mapping.fieldname);
      if (action.type === 'lookup') {
        if (!['value', 'options'].includes(mapping.mode)) errors[`${rowKey}.mode`] = 'select_mode';
        if (!String(mapping.valuePath || '').trim()) errors[`${rowKey}.valuePath`] = 'enter_path';
      } else if (action.type === 'set_values') {
        if (!['source', 'field', 'constant'].includes(mapping.valueFrom || 'constant')) errors[`${rowKey}.valueFrom`] = 'select_value_source';
        if (mapping.valueFrom === 'field' && !names.has(mapping.sourceFieldname)) errors[`${rowKey}.sourceFieldname`] = 'select_target';
        if (!mapping.valueFrom || mapping.valueFrom === 'constant') {
          if (mapping.value === undefined || mapping.value === null || String(mapping.value).trim() === '') errors[`${rowKey}.value`] = 'enter_value';
          else if (mapping.valueType === 'number' && !Number.isFinite(Number(mapping.value))) errors[`${rowKey}.value`] = 'invalid_number';
          else if (mapping.valueType === 'boolean' && ![true, false, 'true', 'false'].includes(mapping.value)) errors[`${rowKey}.value`] = 'invalid_boolean';
        }
      }
    });
  });
  return errors;
};
